/** Media library: every image in the site's media folders, where it is used, upload, replace, delete; and the picker. */
import { useMemo, useRef, useState } from 'react';
import { basename, dirname, mediaValue, type MediaConfig, type MediaItem, type Usage } from '@hotpot/core';
import { Layout, norm } from '../components/Layout.js';
import { ChefHat, Dialog, ErrorBox, Loading, RepoImage, useConfirm } from '../components/ui.js';
import { useT } from '../i18n.js';
import { fileSize } from '../lib/format.js';
import { ImageError, prepareImage, uniqueName } from '../lib/images.js';
import { Link } from '../router.js';
import { useAsync, useSite, useToast } from '../state.js';
import { routeForPath } from './SiteHome.js';
import { errorText } from './Login.js';

function folderLabel(folder: string): string {
  return folder.replace(/^src\/content\//, '').replace(/^public\//, '/');
}

export function Media() {
  const { t } = useT();
  const site = useSite();
  const toast = useToast();
  const [confirm, confirmDialog] = useConfirm();
  const data = useAsync(async () => {
    const [items, usage] = await Promise.all([site.content.listMedia(), site.content.mediaUsage()]);
    return { items, usage };
  }, [site.version]);
  const folders = site.content.mediaFolders();
  const [folder, setFolder] = useState('');
  const [q, setQ] = useState('');
  const [open, setOpen] = useState<MediaItem | null>(null);
  const [uploadFolder, setUploadFolder] = useState(folders[0]?.folder ?? '');
  const [busy, setBusy] = useState(false);
  const input = useRef<HTMLInputElement>(null);

  const shown = useMemo(() => {
    let list = data.data?.items ?? [];
    if (folder) list = list.filter((i) => dirname(i.path) === folder);
    if (q.trim()) list = list.filter((i) => norm(basename(i.path)).includes(norm(q.trim())));
    return list;
  }, [data.data, folder, q]);

  const upload = async (files: File[]) => {
    if (!files.length) return;
    setBusy(true);
    try {
      const existing = new Set((data.data?.items ?? []).filter((i) => dirname(i.path) === uploadFolder).map((i) => basename(i.path)));
      const changes = [];
      for (const f of files) {
        const img = await prepareImage(f, site.config.uploads);
        const name = uniqueName(img.name, existing);
        existing.add(name);
        changes.push({ path: `${uploadFolder}/${name}`, base64: img.base64, previousSha: null });
      }
      const commit = await site.handle.storage.commit(changes, changes.length === 1 ? `Upload ${basename(changes[0]!.path)}` : `Upload ${changes.length} images`, site.author);
      site.afterCommit(commit);
      toast(t('uploaded'));
    } catch (e) {
      toast(e instanceof ImageError ? (e.code === 'too_big' ? t('imageTooBig', { name: e.fileName, mb: e.limitMb }) : t('notAnImage', { name: e.fileName })) : errorText(e, t));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Layout>
      <div className="title-row">
        <div>
          <h1>{t('mediaTitle')}</h1>
          <p className="desc">{t('mediaIntro')}</p>
        </div>
        <div className="actions">
          <button className="btn" onClick={() => input.current?.click()} disabled={busy}>
            {busy ? t('processing') : `${t('upload')}+`}
          </button>
          <input ref={input} type="file" accept="image/*" multiple hidden onChange={async (e) => { await upload([...(e.target.files ?? [])]); e.target.value = ''; }} />
        </div>
      </div>
      <div className="toolbar">
        <input className="input" type="search" placeholder={t('search')} value={q} onChange={(e) => setQ(e.target.value)} aria-label={t('search')} />
        <select className="select" value={folder} onChange={(e) => setFolder(e.target.value)} aria-label={t('allFolders')}>
          <option value="">{t('allFolders')}</option>
          {folders.map((f) => (
            <option key={f.folder} value={f.folder}>
              {folderLabel(f.folder)}
            </option>
          ))}
        </select>
        <label>
          <span className="muted">{t('uploadTo')}</span>
          <select className="select" value={uploadFolder} onChange={(e) => setUploadFolder(e.target.value)}>
            {folders.map((f) => (
              <option key={f.folder} value={f.folder}>
                {folderLabel(f.folder)}
              </option>
            ))}
          </select>
        </label>
        <span className="grow" />
        {data.data && <span className="muted">{t('items', { n: shown.length })}</span>}
      </div>
      {data.error ? (
        <ErrorBox error={data.error} onRetry={data.reload} />
      ) : !data.data ? (
        <Loading />
      ) : shown.length === 0 ? (
        <div className="empty">
          <ChefHat />
          <p>{q ? t('emptySearch', { q }) : t('emptyCollection')}</p>
        </div>
      ) : (
        <div className="media-grid">
          {shown.map((item) => {
            const used = data.data!.usage.get(item.path)?.length ?? 0;
            return (
              <button key={item.path} className="media-tile" onClick={() => setOpen(item)}>
                <span className={`im${item.path.endsWith('.svg') ? ' checker' : ''}`}>
                  <RepoImage path={item.path} alt={basename(item.path)} />
                </span>
                <span className="nm">{basename(item.path)}</span>
                <span className="us">{used ? t('usedInN', { n: used }) : t('unused')}</span>
              </button>
            );
          })}
        </div>
      )}
      {open && data.data && (
        <MediaDetail
          item={open}
          usage={data.data.usage.get(open.path) ?? []}
          onClose={() => setOpen(null)}
          onDelete={async () => {
            if (!(await confirm({ title: `${t('deleteImage')}?`, text: basename(open.path), ok: t('deleteForever') }))) return;
            try {
              const commit = await site.handle.storage.commit([{ path: open.path, delete: true, previousSha: open.sha }], `Delete ${basename(open.path)}`, site.author);
              site.afterCommit(commit);
              setOpen(null);
              toast(t('deleted'));
            } catch (e) {
              toast(errorText(e, t));
            }
          }}
          onReplace={async (file) => {
            try {
              const img = await prepareImage(file, site.config.uploads, basename(open.path));
              const commit = await site.handle.storage.commit([{ path: open.path, base64: img.base64, previousSha: open.sha }], `Replace ${basename(open.path)}`, site.author);
              site.afterCommit(commit);
              setOpen(null);
              toast(t('uploaded'));
            } catch (e) {
              toast(errorText(e, t));
            }
          }}
        />
      )}
      {confirmDialog}
    </Layout>
  );
}

function MediaDetail({ item, usage, onClose, onDelete, onReplace }: { item: MediaItem; usage: Usage[]; onClose: () => void; onDelete: () => void; onReplace: (f: File) => void }) {
  const { t } = useT();
  const site = useSite();
  const input = useRef<HTMLInputElement>(null);
  const routeOf = (u: Usage) => routeForPath(site.config, site.handle.site.id, u.path);
  return (
    <Dialog
      title={basename(item.path)}
      wide
      onClose={onClose}
      buttons={
        <>
          <button className="link-btn danger" onClick={onDelete} disabled={usage.length > 0} title={usage.length ? t('deleteImageUsed') : undefined}>
            {t('deleteImage')}
          </button>
          <button className="link-btn" onClick={onClose}>
            {t('close')}
          </button>
          <button className="btn" onClick={() => input.current?.click()}>
            {t('replaceImage')}
          </button>
          <input ref={input} type="file" accept="image/*" hidden onChange={(e) => e.target.files?.[0] && onReplace(e.target.files[0])} />
        </>
      }
    >
      <div className="media-detail">
        <div className={`preview${item.path.endsWith('.svg') ? ' checker' : ''}`}>
          <RepoImage path={item.path} alt={basename(item.path)} />
        </div>
        <div className="small">
          <p className="muted" style={{ wordBreak: 'break-all', marginTop: 0 }}>
            {item.path}
            {item.size ? ` · ${fileSize(item.size)}` : ''}
          </p>
          <h3 className="section-h" style={{ fontSize: 18 }}>{t('usedIn')}</h3>
          {usage.length === 0 ? (
            <p className="muted">{t('unused')}</p>
          ) : (
            <ul style={{ paddingLeft: 18, lineHeight: 1.6 }}>
              {usage.map((u) => {
                const to = routeOf(u);
                const c = site.config.collections.find((x) => x.name === u.collection);
                return (
                  <li key={u.path}>
                    {to ? <Link to={to} onClick={onClose}>{u.title}</Link> : u.title} <span className="muted">· {c?.label}</span>
                  </li>
                );
              })}
            </ul>
          )}
          {usage.length > 0 && <p className="hint">{t('deleteImageUsed')}</p>}
          <p className="hint">{t('replaceNote')}</p>
        </div>
      </div>
    </Dialog>
  );
}

/** Pick existing images from a field's media folder. */
export function MediaPicker({ media, multiple, onClose }: { media: MediaConfig; multiple: boolean; onClose: (values: string[] | null) => void }) {
  const { t } = useT();
  const site = useSite();
  const items = useAsync(() => site.handle.storage.list(media.folder), [media.folder, site.version]);
  const [sel, setSel] = useState<string[]>([]);
  const [q, setQ] = useState('');
  const list = (items.data ?? []).filter((f) => /\.(jpe?g|png|gif|webp|avif|svg)$/i.test(f.path) && (!q || norm(basename(f.path)).includes(norm(q))));
  return (
    <Dialog
      title={t('chooseFromLibrary')}
      wide
      onClose={() => onClose(null)}
      buttons={
        <>
          <button className="link-btn" onClick={() => onClose(null)}>
            {t('cancel')}
          </button>
          <button className="btn" disabled={!sel.length} onClick={() => onClose(sel.map((p) => mediaValue(basename(p), media)))}>
            {t('pick')}
            {multiple && sel.length > 1 ? ` (${sel.length})` : ''}
          </button>
        </>
      }
    >
      <div className="toolbar" style={{ marginTop: 0 }}>
        <input className="input" type="search" placeholder={t('search')} value={q} onChange={(e) => setQ(e.target.value)} aria-label={t('search')} />
        <span className="muted">{folderLabel(media.folder)}</span>
      </div>
      {items.error ? (
        <ErrorBox error={items.error} />
      ) : !items.data ? (
        <Loading />
      ) : (
        <div className="media-grid" style={{ maxHeight: '55vh', overflow: 'auto', padding: 4 }}>
          {list.map((f) => {
            const on = sel.includes(f.path);
            return (
              <button
                key={f.path}
                className={`media-tile${on ? ' sel' : ''}`}
                aria-pressed={on}
                onClick={() => setSel(multiple ? (on ? sel.filter((p) => p !== f.path) : [...sel, f.path]) : [f.path])}
                onDoubleClick={() => !multiple && onClose([mediaValue(basename(f.path), media)])}
              >
                <span className={`im${f.path.endsWith('.svg') ? ' checker' : ''}`}>
                  <RepoImage path={f.path} alt={basename(f.path)} />
                </span>
                <span className="nm">{basename(f.path)}</span>
              </button>
            );
          })}
          {list.length === 0 && <p className="muted">{t('emptyCollection')}</p>}
        </div>
      )}
    </Dialog>
  );
}
